import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendar, FaUser, FaGraduationCap, FaFileAlt, FaChevronDown, FaChevronRight, FaSync, FaEdit, FaFileExcel } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { getApplications, updateApplication } from '../../utils/applicationsApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useMasterData } from '../../hooks/useMasterData';
import { fetchMasterData } from '../../store/slices/masterDataSlice';
import { API_BASE_URL, getAuthHeaders } from '../../utils/apiConfig';
import { getSecureFileUrl, getSecureFileUrlWithToken } from '../../utils/secureFileAccess';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import { hasPermission } from '../../utils/permissions';

/** Age in full years from date_of_birth (for display; server uses TIMESTAMPDIFF for filters). */
function calcAgeFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const md = today.getMonth() - d.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

/** Same values as registration form location preference selects */
const APPLICATION_LOCATION_CAMPUS_OPTIONS = [
  'Main Campus Thorek',
  'Evergreen Campus',
];

const Container = styled.div`
  padding: 30px 0 0 20px;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    padding: 20px 15px 0 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px 0 10px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.7rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
`;

const RefreshButton = styled.button`
  background:rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgb(11, 23, 92);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.9rem;
    gap: 6px;
  }
`;

const ExportButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.9rem;
    gap: 6px;
  }
`;

const ExportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  padding: 20px;
`;

const ExportModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 560px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ExportModalTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ExportModalFilterTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ExportModalFilterTab = styled.button`
  padding: 8px 14px;
  border: 1px solid #dee2e6;
  background: ${props => props.active ? 'rgb(51, 66, 147)' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? 'rgb(51, 66, 147)' : '#f8f9fa'};
    border-color: rgb(51, 66, 147);
    color: ${props => props.active ? 'white' : 'rgb(51, 66, 147)'};
  }
`;

const ExportModalList = styled.div`
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
  max-height: 320px;
  min-height: 120px;
`;

const ExportModalDateGroup = styled.div`
  &:not(:first-child) {
    border-top: 1px solid #e9ecef;
  }
`;

const ExportModalDateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: #f1f3f5;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ExportModalDateSelectLink = styled.button`
  background: none;
  border: none;
  color: rgb(51, 66, 147);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0;
  text-decoration: underline;
  flex-shrink: 0;

  &:hover {
    color: rgb(11, 23, 92);
  }
`;

const ExportModalRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
  font-size: 0.9rem;
  color: #2c3e50;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }

  input {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ExportModalSelectAll = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 0.9rem;
`;

const ExportModalSelectLink = styled.button`
  background: none;
  border: none;
  color: rgb(51, 66, 147);
  cursor: pointer;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: rgb(11, 23, 92);
  }
`;

const ExportModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
`;

const ExportSelectedButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SearchContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 15px;
  }
`;

const SearchBarStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SearchBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 12px;
`;

const SearchTextGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 260px;
  min-width: 0;
`;

const SearchLabel = styled.span`
  background: #e9ecef;
  padding: 8px 15px;
  border-radius: 6px;
  font-weight: 600;
  color: #495057;
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.9rem;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const ClearButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 480px) {
    padding: 6px 10px;
  }
`;

const FilterGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  min-width: 0;
`;

const FilterField = styled.input`
  padding: 8px 10px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  width: 132px;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 10px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  width: 132px;
  background: white;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FilterClearButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;

  &:hover {
    background: #5a6268;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 15px;
  width: calc(100vw - 360px);
  bottom: 0; 
  z-index: 10;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;

const TableLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;
  border-radius: 15px;
  pointer-events: none;
`;

const FetchErrorBanner = styled.div`
  padding: 12px 16px;
  margin: 0 0 16px 0;
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.95rem;
`;

const ErrorRetryButton = styled.button`
  background: #721c24;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;

  &:hover {
    background: #4a1419;
  }
`;

const TableControls = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 480px) {
    padding: 12px 15px;
  }
`;

const CheckboxInput = styled.input`
  transform: scale(1.2);
`;

const TableWrapper = styled.div`
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
  
  @media (max-width: 768px) {
    min-width: auto;
    display: block;
  }
`;

const Thead = styled.thead`
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Th = styled.th`
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.85rem;
  white-space: nowrap;
  min-width: 140px;
  
  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 0.8rem;
    min-width: 100px;
  }
`;

const SortableHeader = styled(Th)`
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #e9ecef;
  }
`;

const Tbody = styled.tbody`
  @media (max-width: 768px) {
    display: block;
  }
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
  
  @media (max-width: 768px) {
    display: block;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 15px;
    padding: 15px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const Td = styled.td`
  padding: 15px 12px;
  color: #495057;
  font-size: 0.9rem;
  border-right: 1px solid #f1f3f4;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: 8px 0;
    border-right: none;
    border-bottom: 1px solid #f1f3f4;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:before {
      content: attr(data-label) ": ";
      font-weight: 600;
      color: #2c3e50;
      display: inline-block;
      width: 120px;
      margin-right: 10px;
    }
  }
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 5px;
  
  &:hover {
    color: rgb(51, 66, 147);
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    font-size: 1.1rem;
  }
`;

const EmailLink = styled.a`
  color: rgb(51, 66, 147);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
    word-break: break-word;
  }
`;

const PhoneLink = styled.a`
  color: rgb(51, 66, 147);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const PreferenceText = styled.span`
  color: #495057;
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'approved': return '#28a745';
      case 'pending': return '#fff3cd';
      case 'rejected': return '#dc3545';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'approved': return '#ffffff';
      case 'pending': return '#856404';
      case 'rejected': return '#ffffff';
      default: return '#6c757d';
    }
  }};
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 10px;
  }
`;

const ExpandedRow = styled.tr`
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    display: block;
    background: transparent;
    margin-top: 15px;
  }
`;

const ExpandedCell = styled.td`
  padding: 20px;
  border-top: 1px solid #e9ecef;
  
  @media (max-width: 768px) {
    padding: 0;
    border-top: none;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const DetailSection = styled.div`
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    gap: 6px;
  }
`;

const DetailItem = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 12px;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  min-width: 120px;
  
  @media (max-width: 480px) {
    min-width: auto;
    font-size: 0.75rem;
    color: #2c3e50;
  }
`;

const DetailValue = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    color: #495057;
    word-break: break-word;
  }
`;

const FileLink = styled.a`
  color: rgb(51, 66, 147);
  text-decoration: none;
  font-size: 0.8rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    word-break: break-all;
  }
`;

const AcceptButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
    padding: 8px 8px;
    font-size: 0.8rem;
  }
`;

const RejectButton = styled.button`
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #1a2533;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
    padding: 8px 8px;
    font-size: 0.8rem;
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #c82333;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
    padding: 8px 8px;
    font-size: 0.8rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    height: 150px;
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid rgb(11, 23, 92);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  margin: 20px 0;
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 15px;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
    margin-bottom: 12px;
  }
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
`;

const EmptyStateMessage = styled.p`
  margin: 0 0 25px 0;
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 20px;
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 18px;
    max-width: 350px;
  }
`;

const EmptyStateAction = styled.div`
  margin-top: 10px;
`;

const MobileEmptyState = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    
    .empty-icon {
      font-size: 3rem;
      color: #dee2e6;
      margin-bottom: 15px;
    }
    
    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 10px;
    }
    
    .empty-message {
      font-size: 1rem;
      line-height: 1.5;
    }
  }
`;



const GlobalStyle = styled.div`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 101;
  background: #f8f9fa;
  padding: 0 30px;
  margin: 0 -30px 30px -30px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
    margin: 0 -20px 20px -20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 15px;
    margin: 0 -15px 15px -15px;
  }
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0;
  }
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 1px solid #e9ecef;
  
  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const Tab = styled.button`
  background: ${props => {
    if (props.active) {
      if (props.tabType === 'approved') return '#28a745'; // Green for accepted
      if (props.tabType === 'rejected') return '#dc3545'; // Red for rejected
      return 'rgb(51, 66, 147)'; // Default blue for pending
    }
    return 'transparent';
  }};
  color: ${props => props.active ? 'white' : '#6c757d'};
  border: none;
  padding: 15px 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  position: relative;
  min-width: 140px;
  
  &:hover {
    background: ${props => {
      if (props.active) {
        if (props.tabType === 'approved') return '#28a745';
        if (props.tabType === 'rejected') return '#dc3545';
        return 'rgb(51, 66, 147)';
      }
      return '#f8f9fa';
    }};
    color: ${props => props.active ? 'white' : '#2c3e50'};
  }
  
  &:not(:last-child) {
    border-right: 1px solid #e9ecef;
  }
  
  ${props => props.active && `
    border-bottom: 3px solid ${props.tabType === 'approved' ? '#1e7e34' : 
                               props.tabType === 'rejected' ? '#c82333' : 
                               '#2c4a8f'};
  `}
  
  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 0.9rem;
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 0.85rem;
    min-width: 100px;
    flex: 1;
  }
`;



const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
`;

const ModalMessage = styled.p`
  margin: 0 0 25px 0;
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const ConfirmButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;

const EditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: rgb(11, 23, 92);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    min-width: 100px;
    padding: 8px 8px;
    font-size: 0.8rem;
  }
`;

const EditModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  overflow-y: auto;
  padding: 20px;
`;

const EditModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 20px;
    max-width: 95%;
  }
`;

const EditModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
`;

const EditModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #2c3e50;
  }
`;

const EditForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #e9ecef;
  
  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const FormSectionTitle = styled.h3`
  margin: 0 0 15px 0;
  color: rgb(51, 66, 147);
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
  
  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const FormTextarea = styled.textarea`
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.3s ease;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const FormSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  color: #6c757d;
  font-size: 0.9rem;
  
  &:hover {
    border-color: rgb(51, 66, 147);
    color: rgb(51, 66, 147);
    background: #f0f2ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const CurrentFileLink = styled.a`
  font-size: 0.85rem;
  color: rgb(51, 66, 147);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ModalButtonGroup = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 2px solid #e9ecef;
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const SaveEditButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CancelEditButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  background: white;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: rgb(51, 66, 147);
    color: rgb(51, 66, 147);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.95rem;
  }
`;

const ItemsPerPageSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 2px rgba(51, 66, 147, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 0.95rem;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PageNumber = styled.button`
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: ${props => props.active ? 'rgb(51, 66, 147)' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 36px;

  &:hover:not(:disabled) {
    background: ${props => props.active ? 'rgb(41, 56, 137)' : '#f8f9fa'};
    border-color: ${props => props.active ? 'rgb(51, 66, 147)' : 'rgb(51, 66, 147)'};
    color: ${props => props.active ? 'white' : 'rgb(51, 66, 147)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AllAppliedCandidates = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [daysPreferred, setDaysPreferred] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [filledOutFilter, setFilledOutFilter] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // Default to pending applications
  const [showEditModal, setShowEditModal] = useState(false);
  const [applicationToEdit, setApplicationToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editFiles, setEditFiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportModalApplications, setExportModalApplications] = useState([]);
  const [exportSelectedIds, setExportSelectedIds] = useState(new Set());
  const [exportModalFilterType, setExportModalFilterType] = useState('all');
  const [loadingExportModal, setLoadingExportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const { students, isLoading, courses, sessions } = useMasterData();
  const { token, user } = useSelector(state => state.auth);
  const canDeleteApplication = hasPermission(user, 'applications_delete');
  const canUpdateApplication = hasPermission(user, 'applications_update');

  // Path validator function to remove server path prefixes
  const validateFilePath = (filePath) => {
    if (!filePath) return '';
    
    // Remove the /home/cclcusa/public_html/ prefix if it exists
    if (filePath.startsWith('/home/cclcusa/public_html/')) {
      const cleanedPath = filePath.substring('/home/cclcusa/public_html/'.length);
      
      // Now remove admin/backend/uploads/ if it still exists
      if (cleanedPath.includes('admin/backend/uploads/')) {
        const finalPath = cleanedPath.replace('admin/backend/uploads/', '');
        return finalPath;
      }
      
      return cleanedPath;
    }
    
    // Remove the /opt/render/project/src/ prefix if it exists
    if (filePath.startsWith('/opt/render/project/src/')) {
      const cleanedPath = filePath.substring('/opt/render/project/src/'.length);
      
      // Now remove admin/backend/uploads/ if it still exists
      if (cleanedPath.includes('admin/backend/uploads/')) {
        const finalPath = cleanedPath.replace('admin/backend/uploads/', '');
        return finalPath;
      }
      
      return cleanedPath;
    }
    
    // If the path contains 'uploads/', extract only the part after it
    if (filePath.includes('uploads/')) {
      const uploadsIndex = filePath.indexOf('uploads/');
      const cleanedPath = filePath.substring(uploadsIndex + 'uploads/'.length);
      return cleanedPath;
    }
    
    return filePath;
  };

  // Function to build the complete file URL
  const buildFileUrl = (filePath) => {
    if (!filePath) return '';
    
    const validatedPath = validateFilePath(filePath);
    // Use secure file access for ALL files now
    return getSecureFileUrlWithToken(validatedPath, token);
  };

  const applicationFilters = useMemo(
    () => ({
      appliedFrom: appliedDateFrom,
      appliedTo: appliedDateTo,
      ageMin,
      ageMax,
      daysPreferred,
      city: cityFilter,
      campus: campusFilter,
      filledOut: filledOutFilter,
    }),
    [appliedDateFrom, appliedDateTo, ageMin, ageMax, daysPreferred, cityFilter, campusFilter, filledOutFilter]
  );

  const applicationFiltersRef = useRef(applicationFilters);
  applicationFiltersRef.current = applicationFilters;

  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const fetchRequestIdRef = useRef(0);

  const fetchApplications = async (pageOverride) => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      
      // Map tab values to status values for the API
      let statusParam = '';
      if (activeTab === 'pending') {
        statusParam = 'pending';
      } else if (activeTab === 'approved') {
        statusParam = 'Approved';
      } else if (activeTab === 'rejected') {
        statusParam = 'Rejected';
      }

      const page = typeof pageOverride === 'number' ? pageOverride : currentPage;
      
      const response = await getApplications(
        searchTermRef.current,
        statusParam,
        page,
        itemsPerPage,
        applicationFiltersRef.current
      );
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      if (response.success) {
        setError(null);
        setApplications(response.applications);
        // Update pagination from API response
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError('Failed to fetch applications');
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      console.error('Error fetching applications:', error);
      setError('Error fetching applications');
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const filterEffectDidMount = useRef(false);
  const searchDebounceSkipMountRef = useRef(true);

  // Debounced search when searchTerm changes (skip initial mount — tab effect loads data; avoids a stale
  // delayed fetch overwriting age/date filters that use refs + filter effect).
  useEffect(() => {
    if (searchDebounceSkipMountRef.current) {
      searchDebounceSkipMountRef.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 when search changes
      fetchApplications(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch applications when tab changes
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when tab changes
    fetchApplications(1);
  }, [activeTab]);

  // Fetch applications when date/age filters change (skip initial mount; tab effect loads first)
  useEffect(() => {
    if (!filterEffectDidMount.current) {
      filterEffectDidMount.current = true;
      return;
    }
    setCurrentPage(1);
    fetchApplications(1);
  }, [appliedDateFrom, appliedDateTo, ageMin, ageMax, daysPreferred, cityFilter, campusFilter, filledOutFilter]);

  const pageEffectDidMountRef = useRef(false);

  // Fetch when page or page size changes (skip first run — tab effect loads initial data; avoids racing an unfiltered response past a filtered one)
  useEffect(() => {
    if (!pageEffectDidMountRef.current) {
      pageEffectDidMountRef.current = true;
      return;
    }
    fetchApplications();
  }, [currentPage, itemsPerPage]);



  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setAgeMin('');
    setAgeMax('');
    setDaysPreferred('');
    setCityFilter('');
    setCampusFilter('');
    setFilledOutFilter('');
  };

  const hasActiveFilters =
    Boolean(appliedDateFrom) ||
    Boolean(appliedDateTo) ||
    (ageMin !== '' && ageMin !== undefined) ||
    (ageMax !== '' && ageMax !== undefined) ||
    Boolean(daysPreferred) ||
    Boolean(cityFilter.trim()) ||
    Boolean(campusFilter) ||
    Boolean(filledOutFilter);

  const hasAnyFilterOrSearch = Boolean(searchTerm.trim()) || hasActiveFilters;

  const handleSaveChanges = () => {
    // Save changes
  };

  const handleRemoveChanges = () => {
    // Remove changes
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await fetchApplications();
      showSuccess('Applications refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing applications:', error);
      showError('Failed to refresh applications');
    }
  };

  const getStatusParamForFilter = (filterType) => {
    switch (filterType) {
      case 'all': return '';
      case 'new': return 'pending';
      case 'accepted': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return '';
    }
  };

  const fetchExportModalApplications = async (filterType) => {
    setLoadingExportModal(true);
    const statusParam = getStatusParamForFilter(filterType);
    try {
      const response = await getApplications('', statusParam, 1, 10000, applicationFiltersRef.current);
      if (!response.success || !response.applications) {
        showError('Failed to load applications for export');
        setExportModalApplications([]);
        setExportSelectedIds(new Set());
        return;
      }
      const list = response.applications;
      setExportModalApplications(list);
      setExportSelectedIds(new Set(list.map(app => app.id)));
    } catch (error) {
      console.error('Error loading applications for export:', error);
      showError('Failed to load applications for export');
      setExportModalApplications([]);
      setExportSelectedIds(new Set());
    } finally {
      setLoadingExportModal(false);
    }
  };

  const openExportModal = () => {
    setExportModalFilterType('all');
    setShowExportModal(true);
    setExportModalApplications([]);
    setExportSelectedIds(new Set());
    fetchExportModalApplications('all');
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setExportModalApplications([]);
    setExportSelectedIds(new Set());
    setExportModalFilterType('all');
  };

  const handleExportModalFilterChange = (filterType) => {
    setExportModalFilterType(filterType);
    fetchExportModalApplications(filterType);
  };

  const toggleExportSelectAll = () => {
    if (exportSelectedIds.size === exportModalApplications.length) {
      setExportSelectedIds(new Set());
    } else {
      setExportSelectedIds(new Set(exportModalApplications.map(app => app.id)));
    }
  };

  const toggleExportSelectOne = (id) => {
    setExportSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExportSelectByDate = (applicationIds) => {
    if (!applicationIds.length) return;
    const allSelected = applicationIds.every((id) => exportSelectedIds.has(id));
    setExportSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        applicationIds.forEach((id) => next.delete(id));
      } else {
        applicationIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const applicationsByDate = useMemo(() => {
    const groups = {};
    exportModalApplications.forEach((app) => {
      const d = app.created_at ? new Date(app.created_at) : null;
      const key = d ? d.toISOString().slice(0, 10) : 'No date';
      const dateDisplay = d
        ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'No date';
      if (!groups[key]) {
        groups[key] = { dateKey: key, dateDisplay, applications: [] };
      }
      groups[key].applications.push(app);
    });
    return Object.values(groups).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [exportModalApplications]);

  const handleExportSelected = () => {
    const selected = exportModalApplications.filter(app => exportSelectedIds.has(app.id));
    if (selected.length === 0) {
      showError('Please select at least one application to export');
      return;
    }
    try {
      setIsExporting(true);
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit'
        });
      };
      const excelData = selected.map(app => ({
        'First Name': app.first_name || '',
        'Last Name': app.last_name || '',
        'Email': app.email_address || '',
        'Phone Number': app.contact_number || '',
        'Application Date': formatDate(app.created_at),
        'Location Preference 1': app.location_preference_1 || ''
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `applications_export_${timestamp}.xlsx`;
      XLSX.writeFile(workbook, filename);
      showSuccess(`Successfully exported ${selected.length} application(s) to Excel`);
      closeExportModal();
    } catch (error) {
      console.error('Error exporting applications:', error);
      showError('Failed to export applications. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedRows(new Set()); // Reset expanded rows when changing tabs
    setSearchTerm(''); // Clear search when changing tabs
  };



  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const toggleExpandedRow = (applicationId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const handleAcceptApplication = async (applicationId) => {
    try {
      // First, update the application status to approved
      const statusResponse = await fetch(`${API_BASE_URL}/applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: 'Approved' })
      });

      const statusData = await statusResponse.json();

      if (!statusData.success) {
        showError(statusData.message || 'Failed to accept application');
        return;
      }

      // Get the application data to register as student
      const applicationResponse = await fetch(`${API_BASE_URL}/applications/${applicationId}`, { headers: { ...getAuthHeaders() } });
      const applicationData = await applicationResponse.json();

      if (!applicationData.success) {
        showError('Failed to fetch application data for student registration');
        return;
      }

      const application = applicationData.application;

      // Helper function to format dates properly
      const formatDateForDatabase = (dateString) => {
        if (!dateString) return null;
        try {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
        return null;
      };

      // Format all date fields properly
      const formattedDateOfBirth = formatDateForDatabase(application.date_of_birth);
      const formattedTaraItaCompletionDate = formatDateForDatabase(application.tara_ita_packet_date);
      const formattedInfoSessionDate = formatDateForDatabase(application.info_session_date);
      const formattedDateOfJoining = formatDateForDatabase(application.date_of_joining);

      // Register the applicant as a student
      const studentResponse = await fetch(`${API_BASE_URL}/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          firstName: application.first_name || '',
          lastName: application.last_name || '',
          email: application.email_address || '',
          phone: application.contact_number || '',
          dateOfBirth: formattedDateOfBirth,
          course: application.course || '',
          department: application.department || '',
          studentNotes: application.student_notes || '',
          session: application.session || '',
          semester: application.semester || '',
          registrationNumber: `${application.course || 'UNK'}-${application.session ? application.session.charAt(0) : 'UNK'}-${application.id}`,
          socialSecurityNumber: application.social_security_number || null,
          driversLicense: application.driver_license_number || '',
          emergencyContactInfo: application.emergency_contact_info || '',
          emergencyContactPhone: application.emergency_contact_phone || '',
          otherEmergencyContact: application.other_emergency_contact || '',
          caseworkerName: application.caseworker_name || '',
          workforceCenter: application.workforce_center || '',
          taraItaCompletionDate: formattedTaraItaCompletionDate, // Use formatted date
          infoSessionDate: formattedInfoSessionDate, // Use formatted date
          coursePref1: application.course_interest_1 || '',
          daysPref1: application.days_preferred_1 || '',
          locationPref1: application.location_preference_1 || '',
          coursePref2: application.course_interest_2 || '',
          daysPref2: application.days_preferred_2 || '',
          locationPref2: application.location_preference_2 || '',
          attendedInfoSession: application.attended_info_session || false,
          infoSessionLocation: application.filled_out_where || '',
          additionalComments: application.additional_comments || '',
          signature: application.signature || '',
          addressLine1: application.address || '',
          state: application.state_province || '',
          gender: application.gender === 'Male' || application.gender === 'Female' ? application.gender : 'Other',
          nationality: application.nationality || '',
          dateOfJoining: formattedDateOfJoining, // Use formatted date
          status: 'Active',
          // File uploads from application - send as regular fields since they're already uploaded
          photo: application.photo_path || '',
          dlUpload: application.dl_path || '',
          socialSecurityUpload: application.social_security_path || '',
          taraItaPacketUpload: application.tara_ita_path || '',
          voucherDates: application.voucher_dates_path || '',
          // Generate login ID and password for the new student
          loginId: application.login_id || '',
          password: application.password || ''
        })
      });

      const studentData = await studentResponse.json();

      if (studentData.success) {
        // Update local state immediately for better UX
        setApplications(prev => prev.map(app =>
          app.id === applicationId
            ? { ...app, status: 'approved' }
            : app
        ));

        // Refetch master data to update students list
        dispatch(fetchMasterData());

        showSuccess(`Application accepted successfully! Student registered with ID: ${studentData.studentId}`);
      } else {
        // If student registration fails, revert the application status
        await fetch(`${API_BASE_URL}/applications/${applicationId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ status: 'Pending' })
        });

        // Show detailed error message
        if (studentData.errors) {
          const errorMessages = studentData.errors.map(err => `${err.param}: ${err.msg}`).join(', ');
          showError(`Validation errors: ${errorMessages}`);
        } else {
          showError(studentData.message || 'Failed to register student. Application status reverted.');
        }
      }
    } catch (error) {
      console.error('Error accepting application:', error);
      showError('Failed to accept application. Please try again.');
    }
  };

  const handleRejectApplication = async (applicationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: 'Rejected' })
      });

      const data = await response.json();

      if (data.success) {
        // Update local state immediately for better UX
        setApplications(prev => prev.map(app =>
          app.id === applicationId
            ? { ...app, status: 'rejected' }
            : app
        ));

        showSuccess('Application rejected successfully!');
      } else {
        showError(data.message || 'Failed to reject application');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      showError('Failed to reject application. Please try again.');
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        }
      });

      const data = await response.json();

      if (data.success) {
        // Remove the application from local state
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        
        // Close the expanded row if it was open
        setExpandedRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(applicationId);
          return newSet;
        });

        showSuccess('Application deleted successfully!');
      } else {
        showError(data.message || 'Failed to delete application');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      showError('Failed to delete application. Please try again.');
    }
  };

  const openDeleteModal = (application) => {
    setApplicationToDelete(application);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setApplicationToDelete(null);
  };

  const confirmDelete = async () => {
    if (applicationToDelete) {
      await handleDeleteApplication(applicationToDelete.id);
      closeDeleteModal();
    }
  };

  // Handle viewing files with proper error handling
  const handleViewFile = async (filePath, fileType) => {
    try {
      if (!filePath) {
        showError('No file path provided');
        return;
      }

      const validatedPath = validateFilePath(filePath);
      const secureUrl = `${API_BASE_URL}/files/${validatedPath}`;
  
      
      // Make the request through axios so interceptors can handle token errors
      const response = await axios.get(secureUrl, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });


      // Determine MIME type from response headers or file extension
      let mimeType = response.headers['content-type'] || response.data.type;
      
      // If no MIME type from response, try to determine from file extension
      if (!mimeType) {
        const fileExtension = validatedPath.split('.').pop()?.toLowerCase();
        switch (fileExtension) {
          case 'jpg':
          case 'jpeg':
            mimeType = 'image/jpeg';
            break;
          case 'png':
            mimeType = 'image/png';
            break;
          case 'gif':
            mimeType = 'image/gif';
            break;
          case 'pdf':
            mimeType = 'application/pdf';
            break;
          case 'doc':
            mimeType = 'application/msword';
            break;
          case 'docx':
            mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            break;
          default:
            mimeType = 'application/octet-stream';
        }
      }

      // Create blob with correct MIME type
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element to trigger download/view
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Try to determine file type and set appropriate attributes
      if (fileType === 'photo' || mimeType.startsWith('image/')) {
        // For images, open in new tab for viewing
        link.target = '_blank';
      } else {
        // For documents, try to download or open
        link.download = validatedPath.split('/').pop() || 'document';
      }
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

    } catch (error) {
      console.error('Error viewing file:', error);
      console.error('Error response:', error.response);
      // The axios interceptor will handle token errors automatically
      if (error.response?.status !== 401) {
        showError('Failed to load file. Please try again.');
      }
    }
  };

  // Edit application handlers
  const openEditModal = (application) => {
    setApplicationToEdit(application);
    
    // Parse address back into components
    const addressParts = application.address ? application.address.split(', ') : [];
    
    setEditFormData({
      firstName: application.first_name || '',
      lastName: application.last_name || '',
      phone: application.contact_number || '',
      email: application.email_address || '',
      dateOfBirth: application.date_of_birth ? application.date_of_birth.split('T')[0] : '',
      dateOfJoining: application.date_of_joining ? application.date_of_joining.split('T')[0] : '',
      addressLine1: addressParts[0] || '',
      city: addressParts[1] || '',
      state: application.state_province || addressParts[2] || '',
      gender: application.gender || '',
      nationality: application.nationality || '',
      course: application.course || '',
      session: application.session || '',
      socialSecurityNumber: application.social_security_number || '',
      emergencyContactInfo: application.emergency_contact_info || '',
      emergencyContactPhone: application.emergency_contact_phone || '',
      otherEmergencyContact: application.other_emergency_contact || '',
      coursePref1: application.course_interest_1 || '',
      daysPref1: application.days_preferred_1 || '',
      locationPref1: application.location_preference_1 || '',
      coursePref2: application.course_interest_2 || '',
      daysPref2: application.days_preferred_2 || '',
      locationPref2: application.location_preference_2 || '',
      attendedInfoSession: application.attended_info_session || '',
      infoSessionLocation: application.filled_out_where || '',
      infoSessionDate: application.info_session_date ? application.info_session_date.split('T')[0] : '',
      caseworkerName: application.caseworker_name || '',
      workForceCenter: application.workforce_center || '',
      taraItaCompletionDate: application.tara_ita_packet_date ? application.tara_ita_packet_date.split('T')[0] : '',
      studentNotes: application.student_notes || '',
      additionalComments: application.additional_comments || '',
    });
    
    setEditFiles({});
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setApplicationToEdit(null);
    setEditFormData({});
    setEditFiles({});
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        showError(`File "${file.name}" is too large. Please select a file smaller than 5MB.`, 'File Too Large');
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      setEditFiles(prev => ({
        ...prev,
        [name]: file
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData();

      // Append all text fields
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key] !== null && editFormData[key] !== undefined) {
          formData.append(key, editFormData[key]);
        }
      });

      // Append files if they were changed
      Object.keys(editFiles).forEach(key => {
        if (editFiles[key]) {
          formData.append(key, editFiles[key]);
        }
      });

      const response = await updateApplication(applicationToEdit.id, formData);

      if (response.success) {
        showSuccess('Application updated successfully!');
        
        // Update the application in local state
        setApplications(prev => prev.map(app =>
          app.id === applicationToEdit.id
            ? { ...app, ...editFormData, updated_at: new Date().toISOString() }
            : app
        ));
        
        closeEditModal();
        
        // Optionally refetch to get the latest data
        await fetchApplications();
      } else {
        showError(response.message || 'Failed to update application');
      }
    } catch (error) {
      console.error('Error updating application:', error);
      
      // Handle specific error types
      if (error.response?.status === 413 || error.message?.includes('File too large') || error.message?.includes('MulterError')) {
        showError('File size is too large. Please select a smaller file (maximum 5MB recommended).', 'File Too Large');
      } else if (error.response?.status === 400) {
        showError(error.response.data?.message || 'Invalid data provided. Please check your inputs.', 'Validation Error');
      } else if (error.response?.status === 401) {
        showError('Your session has expired. Please login again.', 'Session Expired');
      } else if (error.response?.status >= 500) {
        showError('Server error occurred. Please try again later.', 'Server Error');
      } else {
        showError('Failed to update application. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <GlobalStyle style={{ padding: 0 }} />
      <Header>
        <Title>
          {activeTab === 'pending' && 'New Applications'}
          {activeTab === 'approved' && 'Accepted Applications'}
          {activeTab === 'rejected' && 'Rejected Applications'}
        </Title>
        <ActionButtons>
          <RefreshButton 
            onClick={handleRefresh} 
            disabled={loading}
          >
            <FaSync style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
          <ExportButton 
            onClick={openExportModal}
            disabled={isExporting}
          >
            <FaFileExcel />
            {isExporting ? 'Exporting...' : 'Export'}
          </ExportButton>
        </ActionButtons>
      </Header>
      
      <StickyContainer>
        <TabContainer>
          <TabList>
            <Tab 
              active={activeTab === 'pending'} 
              tabType="pending"
              onClick={() => handleTabChange('pending')}
            >
              New Applications
            </Tab>
            <Tab 
              active={activeTab === 'approved'} 
              tabType="approved"
              onClick={() => handleTabChange('approved')}
            >
              Accepted Applications
            </Tab>
            <Tab 
              active={activeTab === 'rejected'} 
              tabType="rejected"
              onClick={() => handleTabChange('rejected')}
            >
              Rejected Applications
            </Tab>
          </TabList>
        </TabContainer>
        
        <SearchContainer>
        <form onSubmit={(e) => e.preventDefault()} style={{ width: '100%' }}>
        <SearchBarStack>
          <SearchBar>
            <SearchTextGroup>
              <SearchInput
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search by name, email, or phone number..."
              />
            </SearchTextGroup>
            <FilterGroup>
              Applied from
              <FilterField
                type="date"
                value={appliedDateFrom}
                onChange={(e) => setAppliedDateFrom(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              Applied to
              <FilterField
                type="date"
                value={appliedDateTo}
                onChange={(e) => setAppliedDateTo(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              Min age
              <FilterField
                type="number"
                min={0}
                max={120}
                placeholder="e.g. 18"
                value={ageMin}
                onChange={(e) => setAgeMin(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              Max age
              <FilterField
                type="number"
                min={0}
                max={120}
                placeholder="e.g. 65"
                value={ageMax}
                onChange={(e) => setAgeMax(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              Days preferred
              <FilterSelect
                value={daysPreferred}
                onChange={(e) => setDaysPreferred(e.target.value)}
              >
                <option value="">All</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekend">Weekend</option>
                <option value="evenings">Evenings</option>
              </FilterSelect>
            </FilterGroup>
            {hasAnyFilterOrSearch && (
              <FilterClearButton type="button" onClick={clearFilters}>
                Clear filters
              </FilterClearButton>
            )}
          </SearchBar>
          <SearchBar>
            <FilterGroup>
              City
              <FilterField
                type="text"
                placeholder="e.g. Chicago"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                style={{ width: 150, maxWidth: '100%' }}
              />
            </FilterGroup>
            <FilterGroup>
              Campus
              <FilterSelect
                value={campusFilter}
                onChange={(e) => setCampusFilter(e.target.value)}
                style={{ minWidth: 200, width: 'min(220px, 100%)' }}
              >
                <option value="">All</option>
                {APPLICATION_LOCATION_CAMPUS_OPTIONS.map((campus) => (
                  <option key={campus} value={campus}>
                    {campus}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              Filled out at
              <FilterSelect
                value={filledOutFilter}
                onChange={(e) => setFilledOutFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="online">Online</option>
                <option value="office">In office</option>
              </FilterSelect>
            </FilterGroup>
          </SearchBar>
        </SearchBarStack>
        </form>
        </SearchContainer>
      </StickyContainer>

      <TableContainer>
        {error && (
          <FetchErrorBanner>
            <span>{error}</span>
            <ErrorRetryButton
              type="button"
              onClick={() => {
                setError(null);
                fetchApplications();
              }}
            >
              Retry
            </ErrorRetryButton>
          </FetchErrorBanner>
        )}
        {/* <TableControls>
          <CheckboxInput type="checkbox" />
        </TableControls> */}

        {loading && applications.length === 0 ? (
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        ) : applications.length === 0 ? (
          <EmptyStateContainer>
            <EmptyStateIcon>📋</EmptyStateIcon>
            <EmptyStateTitle>
              {hasAnyFilterOrSearch ? 'No Applications Found' : 'No Applications'}
            </EmptyStateTitle>
            <EmptyStateMessage>
              {hasAnyFilterOrSearch
                ? 'No applications match your search or filters. Try adjusting your criteria.'
                : 'There are currently no applications in the system.'
              }
            </EmptyStateMessage>
            {hasAnyFilterOrSearch && (
              <EmptyStateAction>
                <ClearButton onClick={clearFilters}>
                  Clear filters
                </ClearButton>
              </EmptyStateAction>
            )}
          </EmptyStateContainer>
        ) : (
          <TableWrapper>
            {loading && (
              <TableLoadingOverlay aria-hidden="true">
                <LoadingSpinner />
              </TableLoadingOverlay>
            )}
            <Table style={{ display: 'inline-table', marginTop: 20 }}>
              <Thead>
                <Tr>
                  <Th style={{ width: '50px' }}>Expand</Th>
                  <SortableHeader>Last Name ↕</SortableHeader >
                  <SortableHeader>First Name ↕</SortableHeader>
                  <SortableHeader>Email Address ↕</SortableHeader>
                  <SortableHeader>Phone Number ↕</SortableHeader>
                  <SortableHeader>Age ↕</SortableHeader>
                  <SortableHeader>Course Interest ↕</SortableHeader>
                  <SortableHeader>Status ↕</SortableHeader>
                  <SortableHeader>Applied Date ↕</SortableHeader>
                </Tr>
              </Thead>
              <Tbody>
                {applications.map((application) => (
                  <React.Fragment key={application.id}>
                    <Tr>
                      <Td data-label="Expand">
                        <ExpandButton onClick={() => toggleExpandedRow(application.id)}>
                          {expandedRows.has(application.id) ? <FaChevronDown /> : <FaChevronRight />}
                        </ExpandButton>
                      </Td>
                      <Td data-label="Last Name" style={{ fontWeight: '500' }}>{application.last_name}</Td>
                      <Td data-label="First Name" style={{ fontWeight: '500' }}>{application.first_name}</Td>
                      <Td data-label="Email Address">
                        <EmailLink href={`mailto:${application.email_address}`} >
                          <FaEnvelope size={12} style={{ marginRight: 10 }} />
                          {application.email_address}
                        </EmailLink>
                      </Td>
                      <Td data-label="Phone Number">
                        <PhoneLink href={`tel:${application.contact_number}`}>
                          <FaPhone size={12} />
                          {application.contact_number}
                        </PhoneLink>
                      </Td>
                      <Td data-label="Age">
                        {(() => {
                          const age = calcAgeFromDob(application.date_of_birth);
                          return age != null ? age : '—';
                        })()}
                      </Td>
                      <Td data-label="Course Interest">
                        <PreferenceText>{application.course_interest_1 || '-'}</PreferenceText>
                      </Td>
                      <Td data-label="Status">
                        <StatusBadge status={application.status || 'pending'}>
                          {application.status || 'pending'}
                        </StatusBadge>
                      </Td>
                      <Td data-label="Applied Date">{formatDateTime(application.created_at)}</Td>
                    </Tr>
                    {expandedRows.has(application.id) && (
                      <ExpandedRow>
                        <ExpandedCell colSpan="10">
                          <DetailGrid>
                            <DetailSection>
                              <SectionTitle>
                                <FaUser />
                                Personal Information
                              </SectionTitle>
                              <DetailItem>
                                <DetailLabel>Date of Birth:</DetailLabel>
                                <DetailValue>{formatDate(application.date_of_birth)}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Gender:</DetailLabel>
                                <DetailValue>{application.gender || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Nationality:</DetailLabel>
                                <DetailValue>{application.nationality || '-'}</DetailValue>
                              </DetailItem>

                              <DetailItem>
                                <DetailLabel>Address:</DetailLabel>
                                <DetailValue>{application.address || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>State/Province:</DetailLabel>
                                <DetailValue>{application.state_province || '-'}</DetailValue>
                              </DetailItem>
                            </DetailSection>

                            <DetailSection>
                              <SectionTitle>
                                <FaGraduationCap />
                                Academic Information
                              </SectionTitle>
                              
                              <DetailItem>
                                <DetailLabel>Session:</DetailLabel>
                                <DetailValue>{application.session || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Course Interest 1:</DetailLabel>
                                <DetailValue>{application.course_interest_1 || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Days Preferred 1:</DetailLabel>
                                <DetailValue>{application.days_preferred_1 || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Location Preference 1:</DetailLabel>
                                <DetailValue>{application.location_preference_1 || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Course Interest 2:</DetailLabel>
                                <DetailValue>{application.course_interest_2 || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Days Preferred 2:</DetailLabel>
                                <DetailValue>{application.days_preferred_2 || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Location Preference 2:</DetailLabel>
                                <DetailValue>{application.location_preference_2 || '-'}</DetailValue>
                              </DetailItem>
                            </DetailSection>

                            <DetailSection>
                              <SectionTitle>
                                <FaPhone />
                                Emergency Contacts
                              </SectionTitle>
                              <DetailItem>
                                <DetailLabel>Name:</DetailLabel>
                                <DetailValue>{application.other_emergency_contact || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Phone Numer:</DetailLabel>
                                <DetailValue>{application.emergency_contact_phone || '-'}</DetailValue>
                              </DetailItem>
                              
                            </DetailSection>

                            <DetailSection>
                              <SectionTitle>
                                <FaFileAlt />
                                Documents & Files
                              </SectionTitle>
                              <DetailItem>
                                <DetailLabel>Photo:</DetailLabel>
                                <DetailValue>
                                  {application.photo_path ? (
                                    <FileLink 
                                      onClick={() => handleViewFile(application.photo_path, 'photo')}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      View Photo
                                    </FileLink>
                                  ) : '-'}
                                </DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Social Security:</DetailLabel>
                                <DetailValue>
                                  {application.social_security_path ? (
                                    <FileLink 
                                      onClick={() => handleViewFile(application.social_security_path, 'document')}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      View Document
                                    </FileLink>
                                  ) : '-'}
                                </DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Driver License:</DetailLabel>
                                <DetailValue>
                                  {application.dl_path ? (
                                    <FileLink 
                                      onClick={() => handleViewFile(application.dl_path, 'document')}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      View Document
                                    </FileLink>
                                  ) : '-'}
                                </DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Last 4 digits of SSN:</DetailLabel>
                                <DetailValue>
                                  {application.social_security_number ? (
                                    <span style={{ fontFamily: 'serif', fontWeight: '600' }}>
                                      {application.social_security_number}
                                    </span>
                                  ) : '-'}
                                </DetailValue>
                              </DetailItem>
                            </DetailSection>

                            <DetailSection>
                              <SectionTitle>
                                <FaCalendar />
                                Additional Information
                              </SectionTitle>
                              <DetailItem>
                                <DetailLabel>Date of Joining:</DetailLabel>
                                <DetailValue>{formatDate(application.date_of_joining)}</DetailValue>
                              </DetailItem>
                             
                         
                              <DetailItem>
                                <DetailLabel>Attended Info Session:</DetailLabel>
                                <DetailValue>{application.attended_info_session || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Filled Out:</DetailLabel>
                                <DetailValue>{application.filled_out_where || '-'}</DetailValue>
                              </DetailItem>
                           
                              <DetailItem>
                                <DetailLabel>Career Coach Name:</DetailLabel>
                                <DetailValue>{application.workforce_center || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Student Notes:</DetailLabel>
                                <DetailValue>{application.student_notes || '-'}</DetailValue>
                              </DetailItem>
                              <DetailItem>
                                <DetailLabel>Additional Comments:</DetailLabel>
                                <DetailValue>{application.additional_comments || '-'}</DetailValue>
                              </DetailItem>
                            </DetailSection>

                            <DetailSection>
                              <SectionTitle>
                                <FaUser />
                                Application Actions
                              </SectionTitle>
                              <DetailItem style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '15px' }}>
                                <div style={{ display: 'flex', gap: '15px', width: '100%', flexWrap: 'wrap' }}>
                                  {canUpdateApplication && (
                                    <EditButton
                                      onClick={() => openEditModal(application)}
                                    >
                                      <FaEdit />
                                      Edit Application
                                    </EditButton>
                                  )}
                                  {application.status !== 'Approved' && (
                                    <AcceptButton
                                      onClick={() => handleAcceptApplication(application.id)}
                                    >
                                      Accept Application
                                    </AcceptButton>
                                  )}
                                  {canUpdateApplication && application.status !== 'Rejected' && (
                                    <RejectButton
                                      onClick={() => handleRejectApplication(application.id)}
                                    >
                                      Reject Application
                                    </RejectButton>
                                  )}
                                  {canDeleteApplication && (
                                    <DeleteButton
                                      onClick={() => openDeleteModal(application)}
                                    >
                                      Delete Application
                                    </DeleteButton>
                                  )}
                                </div>
                                {application.status && (
                                  <div style={{ marginTop: '10px' }}>
                                    <DetailLabel>Current Status:</DetailLabel>
                                    <StatusBadge status={application.status} style={{ marginLeft: '10px' }}>
                                      {application.status}
                                    </StatusBadge>
                                  </div>
                                )}
                              </DetailItem>
                            </DetailSection>
                          </DetailGrid>
                        </ExpandedCell>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        )}
        
        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
            </PaginationInfo>
            <PaginationControls>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '0.9rem', color: '#6c757d' }}>Items per page:</label>
                <ItemsPerPageSelect
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </ItemsPerPageSelect>
              </div>
              <PaginationButtons>
                <PageButton
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </PageButton>
                <PageNumbers>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <PageNumber
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </PageNumber>
                    );
                  })}
                </PageNumbers>
                <PageButton
                  onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                  disabled={pagination.page >= pagination.totalPages || loading}
                >
                  Next
                </PageButton>
              </PaginationButtons>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableContainer>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalOverlay onClick={closeDeleteModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Delete Application</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete the application for{' '}
              <strong>{applicationToDelete?.first_name} {applicationToDelete?.last_name}</strong>?
              <br />
              This action cannot be undone.
            </ModalMessage>
            <ModalButtons>
              <CancelButton onClick={closeDeleteModal}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={confirmDelete}>
                Delete
              </ConfirmButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Export Applications Modal */}
      {showExportModal && (
        <ExportModalOverlay onClick={closeExportModal}>
          <ExportModalContent onClick={(e) => e.stopPropagation()}>
            <ExportModalTitle>
              Select applications to export
            </ExportModalTitle>
            <ExportModalFilterTabs>
              <ExportModalFilterTab
                type="button"
                active={exportModalFilterType === 'all'}
                onClick={() => handleExportModalFilterChange('all')}
                disabled={loadingExportModal}
              >
                All Applications
              </ExportModalFilterTab>
              <ExportModalFilterTab
                type="button"
                active={exportModalFilterType === 'new'}
                onClick={() => handleExportModalFilterChange('new')}
                disabled={loadingExportModal}
              >
                New Applications
              </ExportModalFilterTab>
              <ExportModalFilterTab
                type="button"
                active={exportModalFilterType === 'accepted'}
                onClick={() => handleExportModalFilterChange('accepted')}
                disabled={loadingExportModal}
              >
                Accepted
              </ExportModalFilterTab>
              <ExportModalFilterTab
                type="button"
                active={exportModalFilterType === 'rejected'}
                onClick={() => handleExportModalFilterChange('rejected')}
                disabled={loadingExportModal}
              >
                Rejected
              </ExportModalFilterTab>
            </ExportModalFilterTabs>
            {loadingExportModal ? (
              <>
                <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                  Loading applications…
                </div>
                <ExportModalFooter>
                  <CancelButton type="button" onClick={closeExportModal}>
                    Cancel
                  </CancelButton>
                </ExportModalFooter>
              </>
            ) : exportModalApplications.length === 0 ? (
              <>
                <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                  No applications found for this filter.
                </div>
                <ExportModalFooter>
                  <CancelButton type="button" onClick={closeExportModal}>
                    Close
                  </CancelButton>
                </ExportModalFooter>
              </>
            ) : (
              <>
                <ExportModalSelectAll>
                  <ExportModalSelectLink type="button" onClick={toggleExportSelectAll}>
                    {exportSelectedIds.size === exportModalApplications.length
                      ? 'Deselect all'
                      : 'Select all'}
                  </ExportModalSelectLink>
                  <span style={{ color: '#6c757d' }}>
                    {exportSelectedIds.size} of {exportModalApplications.length} selected
                  </span>
                </ExportModalSelectAll>
                <ExportModalList>
                  {applicationsByDate.map(({ dateKey, dateDisplay, applications }) => (
                    <ExportModalDateGroup key={dateKey}>
                      <ExportModalDateHeader>
                        <span>{dateDisplay}</span>
                        <ExportModalDateSelectLink
                          type="button"
                          onClick={() => toggleExportSelectByDate(applications.map((app) => app.id))}
                        >
                          {applications.every((app) => exportSelectedIds.has(app.id))
                            ? 'Deselect all'
                            : 'Select all'}
                        </ExportModalDateSelectLink>
                      </ExportModalDateHeader>
                      {applications.map((app) => (
                        <ExportModalRow key={app.id}>
                          <input
                            type="checkbox"
                            checked={exportSelectedIds.has(app.id)}
                            onChange={() => toggleExportSelectOne(app.id)}
                          />
                          <span>
                            {app.last_name}, {app.first_name} – {app.email_address || app.contact_number || '—'}
                          </span>
                        </ExportModalRow>
                      ))}
                    </ExportModalDateGroup>
                  ))}
                </ExportModalList>
                <ExportModalFooter>
                  <CancelButton type="button" onClick={closeExportModal}>
                    Cancel
                  </CancelButton>
                  <ExportSelectedButton
                    type="button"
                    onClick={handleExportSelected}
                    disabled={exportSelectedIds.size === 0 || isExporting}
                  >
                    <FaFileExcel />
                    {isExporting ? 'Exporting…' : `Export selected (${exportSelectedIds.size})`}
                  </ExportSelectedButton>
                </ExportModalFooter>
              </>
            )}
          </ExportModalContent>
        </ExportModalOverlay>
      )}

      {/* Edit Application Modal */}
      {showEditModal && applicationToEdit && (
        <EditModalOverlay>
          <EditModalContent onClick={(e) => e.stopPropagation()}>
            <EditModalHeader>
              <EditModalTitle>
                Edit Application - {applicationToEdit.first_name} {applicationToEdit.last_name}
              </EditModalTitle>
              <CloseButton onClick={closeEditModal}>
                <FaTimes />
              </CloseButton>
            </EditModalHeader>

            <EditForm onSubmit={handleEditSubmit}>
              {/* Personal Information */}
              <FormSection>
                <FormSectionTitle>
                  <FaUser />
                  Personal Information
                </FormSectionTitle>
                <FormGroup>
                  <FormLabel>First Name *</FormLabel>
                  <FormInput
                    type="text"
                    name="firstName"
                    value={editFormData.firstName || ''}
                    onChange={handleEditFormChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Last Name *</FormLabel>
                  <FormInput
                    type="text"
                    name="lastName"
                    value={editFormData.lastName || ''}
                    onChange={handleEditFormChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Phone *</FormLabel>
                  <FormInput
                    type="tel"
                    name="phone"
                    value={editFormData.phone || ''}
                    onChange={handleEditFormChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Date of Birth</FormLabel>
                  <FormInput
                    type="date"
                    name="dateOfBirth"
                    value={editFormData.dateOfBirth || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Gender</FormLabel>
                  <FormSelect
                    name="gender"
                    value={editFormData.gender || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Nationality</FormLabel>
                  <FormInput
                    type="text"
                    name="nationality"
                    value={editFormData.nationality || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Address</FormLabel>
                  <FormInput
                    type="text"
                    name="addressLine1"
                    value={editFormData.addressLine1 || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>City</FormLabel>
                  <FormInput
                    type="text"
                    name="city"
                    value={editFormData.city || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>State</FormLabel>
                  <FormInput
                    type="text"
                    name="state"
                    value={editFormData.state || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
              </FormSection>

              {/* Academic Information */}
              <FormSection>
                <FormSectionTitle>
                  <FaGraduationCap />
                  Academic Information
                </FormSectionTitle>
                <FormGroup>
                  <FormLabel>Session</FormLabel>
                  <SessionSelect
                    value={editFormData.session || ''}
                    onChange={(value) => setEditFormData(prev => ({ ...prev, session: value }))}
                    placeholder="Select session"
                    isClearable={false}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Course</FormLabel>
                  <CourseSelect
                    value={editFormData.course || ''}
                    onChange={(value) => setEditFormData(prev => ({ ...prev, course: value }))}
                    placeholder="Select course"
                    isClearable={false}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Course Interest 1</FormLabel>
                  <CourseSelect
                    value={editFormData.coursePref1 || ''}
                    onChange={(value) => setEditFormData(prev => ({ ...prev, coursePref1: value }))}
                    placeholder="Select course preference"
                    isClearable={false}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Days Preferred 1</FormLabel>
                  <FormSelect
                    name="daysPref1"
                    value={editFormData.daysPref1 || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">- Select Days -</option>
                    <option value="Weekdays">Weekdays</option>
                    <option value="Weekends">Weekends</option>
                    <option value="Evenings">Evenings</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Location Preference 1</FormLabel>
                  <FormSelect
                    name="locationPref1"
                    value={editFormData.locationPref1 || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">- Select Location -</option>
                    <option value="Main Campus Thorek">Main Campus Thorek</option>
                    <option value="Evergreen Campus">Evergreen Campus</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Course Interest 2</FormLabel>
                  <CourseSelect
                    value={editFormData.coursePref2 || ''}
                    onChange={(value) => setEditFormData(prev => ({ ...prev, coursePref2: value }))}
                    placeholder="Select course preference"
                    isClearable={true}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Days Preferred 2</FormLabel>
                  <FormSelect
                    name="daysPref2"
                    value={editFormData.daysPref2 || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">- Select Days -</option>
                    <option value="Weekdays">Weekdays</option>
                    <option value="Weekends">Weekends</option>
                    <option value="Evenings">Evenings</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Location Preference 2</FormLabel>
                  <FormSelect
                    name="locationPref2"
                    value={editFormData.locationPref2 || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">- Select Location -</option>
                    <option value="Main Campus Thorek">Main Campus Thorek</option>
                    <option value="Evergreen Campus">Evergreen Campus</option>
                  </FormSelect>
                </FormGroup>
              </FormSection>

              {/* Emergency Contacts */}
              <FormSection>
                <FormSectionTitle>
                  <FaPhone />
                  Emergency Contacts
                </FormSectionTitle>
                <FormGroup>
                  <FormLabel>Emergency Contact Name</FormLabel>
                  <FormInput
                    type="text"
                    name="emergencyContactInfo"
                    value={editFormData.emergencyContactInfo || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Emergency Contact Phone</FormLabel>
                  <FormInput
                    type="tel"
                    name="emergencyContactPhone"
                    value={editFormData.emergencyContactPhone || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <FormLabel>Other Emergency Contact</FormLabel>
                  <FormInput
                    type="text"
                    name="otherEmergencyContact"
                    value={editFormData.otherEmergencyContact || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
              </FormSection>

              {/* Documents */}
              <FormSection>
                <FormSectionTitle>
                  <FaFileAlt />
                  Documents
                </FormSectionTitle>
                <FormGroup>
                  <FormLabel>Photo</FormLabel>
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="edit-photo">
                      {editFiles.photo ? editFiles.photo.name : 'Choose new photo (optional)'}
                    </FileInputLabel>
                    <FileInput
                      id="edit-photo"
                      type="file"
                      name="photo"
                      accept="image/*"
                      onChange={handleEditFileChange}
                    />
                    {applicationToEdit.photo_path && (
                      <CurrentFileLink 
                        onClick={() => handleViewFile(applicationToEdit.photo_path, 'photo')}
                        style={{ cursor: 'pointer' }}
                      >
                        View current photo
                      </CurrentFileLink>
                    )}
                  </FileInputWrapper>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Social Security Card</FormLabel>
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="edit-ssn">
                      {editFiles.socialSecurityUpload ? editFiles.socialSecurityUpload.name : 'Choose new document (optional)'}
                    </FileInputLabel>
                    <FileInput
                      id="edit-ssn"
                      type="file"
                      name="socialSecurityUpload"
                      accept="image/*,.pdf"
                      onChange={handleEditFileChange}
                    />
                    {applicationToEdit.social_security_path && (
                      <CurrentFileLink 
                        onClick={() => handleViewFile(applicationToEdit.social_security_path, 'document')}
                        style={{ cursor: 'pointer' }}
                      >
                        View current document
                      </CurrentFileLink>
                    )}
                  </FileInputWrapper>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Driver License</FormLabel>
                  <FileInputWrapper>
                    <FileInputLabel htmlFor="edit-dl">
                      {editFiles.dlUpload ? editFiles.dlUpload.name : 'Choose new document (optional)'}
                    </FileInputLabel>
                    <FileInput
                      id="edit-dl"
                      type="file"
                      name="dlUpload"
                      accept="image/*,.pdf"
                      onChange={handleEditFileChange}
                    />
                    {applicationToEdit.dl_path && (
                      <CurrentFileLink 
                        onClick={() => handleViewFile(applicationToEdit.dl_path, 'document')}
                        style={{ cursor: 'pointer' }}
                      >
                        View current document
                      </CurrentFileLink>
                    )}
                  </FileInputWrapper>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Last 4 Digits of SSN</FormLabel>
                  <FormInput
                    type="text"
                    name="socialSecurityNumber"
                    value={editFormData.socialSecurityNumber || ''}
                    onChange={handleEditFormChange}
                    maxLength="4"
                    pattern="\d{4}"
                  />
                </FormGroup>
              </FormSection>

              {/* Additional Information */}
              <FormSection>
                <FormSectionTitle>
                  <FaCalendar />
                  Additional Information
                </FormSectionTitle>
                <FormGroup>
                  <FormLabel>Date of Joining</FormLabel>
                  <FormInput
                    type="date"
                    name="dateOfJoining"
                    value={editFormData.dateOfJoining || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Info Session Date</FormLabel>
                  <FormInput
                    type="date"
                    name="infoSessionDate"
                    value={editFormData.infoSessionDate || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>TARA/ITA Completion Date</FormLabel>
                  <FormInput
                    type="date"
                    name="taraItaCompletionDate"
                    value={editFormData.taraItaCompletionDate || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Attended Info Session</FormLabel>
                  <FormSelect
                    name="attendedInfoSession"
                    value={editFormData.attendedInfoSession || ''}
                    onChange={handleEditFormChange}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Info Session Location</FormLabel>
                  <FormInput
                    type="text"
                    name="infoSessionLocation"
                    value={editFormData.infoSessionLocation || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Caseworker Name</FormLabel>
                  <FormInput
                    type="text"
                    name="caseworkerName"
                    value={editFormData.caseworkerName || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Workforce Center</FormLabel>
                  <FormInput
                    type="text"
                    name="workForceCenter"
                    value={editFormData.workForceCenter || ''}
                    onChange={handleEditFormChange}
                  />
                </FormGroup>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <FormLabel>Student Notes</FormLabel>
                  <FormTextarea
                    name="studentNotes"
                    value={editFormData.studentNotes || ''}
                    onChange={handleEditFormChange}
                    placeholder="Enter any additional notes about the student..."
                  />
                </FormGroup>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <FormLabel>Additional Comments</FormLabel>
                  <FormTextarea
                    name="additionalComments"
                    value={editFormData.additionalComments || ''}
                    onChange={handleEditFormChange}
                    placeholder="Enter any additional comments..."
                  />
                </FormGroup>
              </FormSection>

              {/* Action Buttons */}
              <ModalButtonGroup>
                <CancelEditButton type="button" onClick={closeEditModal}>
                  Cancel
                </CancelEditButton>
                <SaveEditButton type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </SaveEditButton>
              </ModalButtonGroup>
            </EditForm>
          </EditModalContent>
        </EditModalOverlay>
      )}
    </Container>
  );
};

export default AllAppliedCandidates; 